import { memo } from 'react';
import { FormattedMessage } from 'react-intl';
import ConditionalWidthTableCell from '../../../../core/components/table/ConditionalWidthTableCell';
import TableCellText, {
    TableCellTextWithSubtext,
} from '../../../../core/components/table/TableCellText';
import useCustomerPriceVisibility from '../../../../core/hooks/useCustomerPriceVisibility';
import useLoginState from '../../../../core/hooks/useLoginState';
import usePriceFormatter from '../../../../core/hooks/usePriceFormatter';
import Price from '../../../../pricing/components/Price';
import type { PreorderPriceEditableProduct } from '../../../../pricing/type';
import ProductDiscount from '../ProductDiscount';
import ProductRowDiscounts from './ProductRowDiscounts';
import type { ProductVariantsTableProductRowProps } from './types';

const ProductRowCustomerPriceColumn = ({
    fixedWidth,
    customerPrice,
    showDiscountColumn,
    showInlineDiscount = false,
    onSale = false,
    areAdditionalChargesApplicable = false,
    product,
}: Pick<
    ProductVariantsTableProductRowProps,
    'fixedWidth' | 'customerPrice' | 'showDiscountColumn'
> & {
    areAdditionalChargesApplicable?: boolean;
    product?: PreorderPriceEditableProduct;
    showInlineDiscount?: boolean;
    onSale?: boolean;
}) => {
    const isLoggedIn = useLoginState();
    const [showCustomerPrice] = useCustomerPriceVisibility();
    const priceFormatter = usePriceFormatter();

    if (!isLoggedIn || !showCustomerPrice) {
        return null;
    }

    const formattedCustomerPrice = priceFormatter(
        customerPrice?.finalPrice ?? 0
    );

    return (
        <>
            <ConditionalWidthTableCell fixedWidth={fixedWidth} width="110px">
                <Price
                    product={product}
                    customerPrice={customerPrice}
                    retainPosition={showInlineDiscount}
                    horizontalEdit={!showInlineDiscount}
                >
                    <TableCellTextWithSubtext
                        text={
                            <div>
                                <ProductRowDiscounts
                                    showInlineDiscount={showInlineDiscount}
                                    onSale={onSale}
                                    customerPrice={customerPrice}
                                />
                                <span>
                                    <FormattedMessage
                                        id={formattedCustomerPrice.currency}
                                    />
                                    &nbsp;
                                    {formattedCustomerPrice.price}
                                </span>
                            </div>
                        }
                        subText={
                            <>
                                <FormattedMessage id="plus VAT" />
                                {areAdditionalChargesApplicable && (
                                    <>
                                        <span> </span>
                                        <FormattedMessage id="and additional charges" />
                                    </>
                                )}
                            </>
                        }
                    />
                </Price>
            </ConditionalWidthTableCell>
            {showDiscountColumn && (
                <ConditionalWidthTableCell fixedWidth={fixedWidth} width="60px">
                    <TableCellText>
                        <ProductDiscount customerPrice={customerPrice} />
                    </TableCellText>
                </ConditionalWidthTableCell>
            )}
        </>
    );
};

export default memo(ProductRowCustomerPriceColumn);
